/**
 * Shared axios defaults + one cached `/user/refresh-token` per page load (deduped across all callers).
 * Refresh starts eagerly when this module loads so the first API call rarely waits on the network.
 */
import axios from 'axios'

import config from './config'
import { getOrCreateClientInstallId } from './clientInstallId'
import { getHardwareMacForRequest } from './nativeHardwareMac'
import {
  hasPendingReauthAfterSessionExpiry,
  hasSessionExpiresAt,
  hasPersistedVirtualSessionWallet,
  setSessionExpiresAt,
} from './sessionExpiration'

/**
 * Identity headers the main axios interceptor (`common/api.ts`) adds to every request.
 * This module's eager `/user/refresh-token` runs at import time — often *before* that interceptor
 * is registered — so the refresh POST must attach the same headers or the backend falls back to
 * UA-only fingerprinting and different users on one browser look identical.
 */
function sessionIdentityHeaders(): Record<string, string> {
  const headers: Record<string, string> = {}
  const apiKey = process.env.REACT_APP_X_API_KEY
  if (apiKey) headers['x-api-key'] = apiKey
  const installId = getOrCreateClientInstallId()
  if (installId) headers['x-device-id'] = installId
  const mac = getHardwareMacForRequest()
  if (mac) headers['x-mac-address'] = mac
  return headers
}

axios.defaults.baseURL = `${config.server}${config.baseURL}`
axios.defaults.timeout = 30000
axios.defaults.withCredentials = true

/** Single in-flight / resolved refresh for this page load; cleared on logout / invalidate. */
let bootstrapRefreshOnce: Promise<boolean> | null = null

/** Call after a new session is established (login) so a fresh align can run if needed. */
export function invalidateBootstrapRefresh(): void {
  bootstrapRefreshOnce = null
}

export function resetSessionRefreshDedupe(): void {
  bootstrapRefreshOnce = null
}

function shouldSkipSessionAlign(url: string): boolean {
  return (
    url.includes('/user/refresh-token') ||
    url.includes('/user/clear-auth-cookies')
  )
}

function getBootstrapRefresh(): Promise<boolean> {
  if (bootstrapRefreshOnce) return bootstrapRefreshOnce

  if (hasPendingReauthAfterSessionExpiry()) {
    return Promise.resolve(false)
  }
  if (!hasSessionExpiresAt() && !hasPersistedVirtualSessionWallet()) {
    return Promise.resolve(false)
  }

  bootstrapRefreshOnce = (async () => {
    try {
      const response = await axios.post('/user/refresh-token', undefined, {
        headers: sessionIdentityHeaders(),
      })
      const ok = !!(response.data?.success && response.data?.refreshed)
      const expiresAt = response.data?.expiresAt
      if (typeof expiresAt === 'number' && expiresAt > 0) {
        setSessionExpiresAt(expiresAt)
      }
      return ok
    } catch {
      return false
    }
  })()

  return bootstrapRefreshOnce
}

/**
 * App / manual — same singleton as interceptor (one POST per load unless invalidated).
 */
export const refreshAccessToken = (): Promise<boolean> => getBootstrapRefresh()

/** Single in-flight recovery shared by every request that 401s in the same burst. */
let recoverAfter401Once: Promise<boolean> | null = null

/**
 * Force ONE fresh `/user/refresh-token` after an authed request 401s, ignoring the resolved
 * bootstrap cache (the 401 proves the current access token is dead — e.g. its IP-bound `ctx`
 * no longer matches). The refresh endpoint tolerates IP changes and re-binds the context, so
 * this heals mid-session IP flips without forcing a re-login. Concurrent 401s share one POST;
 * the pending-reauth guard inside `getBootstrapRefresh` keeps a truly dead session from
 * refresh-spamming once the session modal has fired.
 */
export function recoverSessionAfter401(): Promise<boolean> {
  if (recoverAfter401Once) return recoverAfter401Once
  bootstrapRefreshOnce = null
  const attempt = getBootstrapRefresh().finally(() => {
    if (recoverAfter401Once === attempt) recoverAfter401Once = null
  })
  recoverAfter401Once = attempt
  return attempt
}

/**
 * Axios pre-request: await shared bootstrap (instant if prefetch already finished).
 */
export async function alignSessionBeforeRequest(config: { url?: string }): Promise<void> {
  const url = String(config.url || '')
  if (shouldSkipSessionAlign(url)) return
  await getBootstrapRefresh()
}

// Start refresh as early as possible (before React tree / first component HTTP).
if (typeof window !== 'undefined') {
  if (!hasPendingReauthAfterSessionExpiry() && (hasSessionExpiresAt() || hasPersistedVirtualSessionWallet())) {
    void getBootstrapRefresh()
  }
}
